import { useEffect, useState } from "react";

import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ModalContent } from "../components/ModalContent";

import { Button } from "@/components/ui/button";
import {
  Loader2,
  Package,
  FileText,
  Tag,
  Hash,
  Percent,
  Layers3,
  Image as ImageIcon,
  Pencil,
  Plus,
  LayoutGrid,
  CalendarDays,
} from "lucide-react";

import { useDispatch } from "react-redux";

import { showToast } from "@/redux/Toast/actions";
import { callLoader } from "@/redux/Loader/actions";

import {
  insertSubModule,
  updateSubModule,
} from "@/redux/Modules/subModules/actions";

import ImageUploadField from "@/components/common/ImageUploadField";
import { getModuleDropdownList } from "@/redux/Modules/modules/actions";
import { Input, SelectInput } from "../components/FormControls";
import { getBaseModuleDropdown } from "@/redux/Modules/baseModules/actions";

/* ───────────────────────── INITIAL ───────────────────────── */
const initialState = {
  SUBMODULE_NAME: "",
  SUBMODULE_DESCRIPTION: "",
  SUBMODULE_TYPE: "",
  SUBMODULE_BASEPRICE: 0,
  SUBMODULE_BASEPRICETYPE: "PER MONTH",
  SUBMODULE_PERSEATPRICE: 0,
  SUBMODULE_USAGERATE: 0,
  SUBMODULE_PRICINGMODEL: "",
  TRIAL_DAYS: 0,
  SEQUENCE_NO: 0,
  CREATED_BY: "admin",
  MODULE_ID: 0,
  SUBMODULE_ID: 0,
  FILEDATA: null,
};

export default function AddSubModuleModal({
  open,
  onOpenChange,
  onSuccess,
  //   baseModuleId,
  isEdit = false,
  editData = null,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [moduleTypes, setModuleTypes] = useState([]);
  const [baseModuleId, setBaseModuleId] = useState("");

  const [modules, setModules] = useState([]);

  /* ───────────────────────── SET EDIT DATA ───────────────────────── */
  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setPreviewImage(null);
      setErrors({});
      return;
    }

    if (isEdit && editData) {
      setBaseModuleId(editData.BASEMODULE_ID);
      setFormData({
        MODULE_ID: editData?.MODULE_ID || 0,
        SUBMODULE_ID: editData?.SUBMODULE_ID || 0,
        SUBMODULE_NAME: editData?.SUBMODULE_NAME || "",
        SUBMODULE_DESCRIPTION: editData?.SUBMODULE_DESCRIPTION || "",
        SUBMODULE_BASEPRICE: editData?.SUBMODULE_BASEPRICE || 0,
        SUBMODULE_BASEPRICETYPE:
          editData?.SUBMODULE_BASEPRICETYPE || "PER MONTH",
        SUBMODULE_PERSEATPRICE: editData?.SUBMODULE_PERSEATPRICE || 0,
        SUBMODULE_USAGERATE: editData?.SUBMODULE_USAGERATE || 0,
        SUBMODULE_PRICINGMODEL: editData?.SUBMODULE_PRICINGMODEL || "",
        SUBMODULE_TYPE: editData?.SUBMODULE_TYPE || "",
        TRIAL_DAYS: editData?.TRIAL_DAYS || 0,
        SEQUENCE_NO: editData?.SUBMODULE_SEQUENCE_NO || 0,
        CREATED_BY: editData?.CREATED_BY || "admin",
        FILEDATA: null,
        SUBMODULE_LOGO: editData?.SUBMODULE_LOGO || "",
      });

      setPreviewImage(editData?.SUBMODULE_LOGO || null);
    } else {
      setBaseModuleId("");
      setModules([]);
      setFormData(initialState);
      setPreviewImage(null);
    }
  }, [open, isEdit, editData]);

  useEffect(() => {
    if (!baseModuleId) return;

    const loadModules = async () => {
      try {
        const res = await dispatch(getModuleDropdownList(baseModuleId));
        console.log("res: ", res);

        setModules(res);
      } catch (err) {
        console.error(err);
      }
    };

    loadModules();
  }, [baseModuleId, dispatch]);

  useEffect(() => {
    const loadDropdown = async () => {
      try {
        const res = await dispatch(getBaseModuleDropdown());
        setModuleTypes(res);
      } catch (err) {
        console.error(err);
      }
    };

    loadDropdown();
  }, []);

  /* ───────────────────────── HANDLER ───────────────────────── */
  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const err = {};
    if (!formData.SUBMODULE_NAME) err.SUBMODULE_NAME = "Required";
    if (!formData.SUBMODULE_TYPE) err.SUBMODULE_TYPE = "Required";
    if (!formData.SUBMODULE_BASEPRICETYPE)
      err.SUBMODULE_BASEPRICETYPE = "Required";

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  /* ───────────────────────── SUBMIT ───────────────────────── */
  const handleSubmit = async () => {
    try {
      if (!validate()) return;

      setLoading(true);
      dispatch(callLoader(true));

      const payload = new FormData();

      Object.entries(formData).forEach(([k, v]) => {
        if (k === "FILEDATA") {
          payload.append(k, v || "");
        } else {
          payload.append(k, v ?? "");
        }
      });

      if (isEdit) {
        await dispatch(updateSubModule(payload));
        dispatch(showToast("SubModule updated", "success", true));
      } else {
        await dispatch(insertSubModule(payload));
        dispatch(showToast("SubModule created", "success", true));
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      dispatch(
        showToast(e?.message || "Failed to save submodule", "error", true),
      );
    } finally {
      setLoading(false);
      dispatch(callLoader(false));
    }
  };

  /* ───────────────────────── UI ───────────────────────── */
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ModalContent className="h-[90vh] flex flex-col">
        {/* HEADER */}
        <DialogHeader className="shrink-0 relative border-b border-white/10 px-6 py-5 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900">
          <div className="flex items-center gap-4">
            <div
              className="h-11 w-11 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center
        shadow-lg shadow-teal-500/20
        shrink-0
      "
            >
              {isEdit ? (
                <Pencil className="h-5 w-5 text-white" />
              ) : (
                <Plus className="h-5 w-5 text-white" />
              )}
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                {isEdit ? "Update Sub Module" : "Add Sub Module"}
              </DialogTitle>
              <DialogDescription>
                Configure sub module under selected module
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* BODY */}
        <div className="flex-1 grid grid-cols-1 gap-6 overflow-y-auto p-6 lg:grid-cols-2">
          {/* LEFT */}
          <div className="space-y-4">
            <SelectInput
              label="Base Module"
              name="BASEMODULE_ID"
              icon={LayoutGrid}
              value={baseModuleId}
              onValueChange={(name, value) => {
                setBaseModuleId(Number(value));
              }}
              options={[
                { value: "", label: "Select Base Module" },
                ...moduleTypes.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })),
              ]}
            />
            <SelectInput
              label="Select Module"
              name="MODULE_ID"
              icon={LayoutGrid}
              value={formData.MODULE_ID}
              onValueChange={handleChange}
              error={errors.MODULE_ID}
              options={[
                { value: "", label: "Select Module" },
                ...modules.map((item) => ({
                  value: item.VALUE,
                  label: item.TEXT,
                })),
              ]}
            />
            <Input
              label="Sub Module Name"
              name="SUBMODULE_NAME"
              icon={Package}
              value={formData.SUBMODULE_NAME}
              onValueChange={handleChange}
              error={errors.SUBMODULE_NAME}
            />

            <Input
              label="Description"
              name="SUBMODULE_DESCRIPTION"
              textarea
              icon={FileText}
              value={formData.SUBMODULE_DESCRIPTION}
              onValueChange={handleChange}
            />
            <Input
              label="Sub Module Type"
              name="SUBMODULE_TYPE"
              icon={Tag}
              value={formData.SUBMODULE_TYPE}
              onValueChange={handleChange}
              error={errors.SUBMODULE_TYPE}
            />

            {/* <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Type
              </label>

              <select
                value={formData.SUBMODULE_TYPE}
                onChange={(e) => handleChange("SUBMODULE_TYPE", e.target.value)}
                className="
      w-full px-3 py-2
      bg-white dark:bg-slate-900
      border border-slate-200 dark:border-slate-700
      rounded-lg text-sm
      text-slate-900 dark:text-white
      appearance-none cursor-pointer
      focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500
      transition-all
    "
              >
                <option value="">Select Type</option>

                {modules?.map((item) => (
                  <option key={item.VALUE} value={item.TEXT}>
                    {item.TEXT}
                  </option>
                ))}
              </select>

              {errors.SUBMODULE_TYPE && (
                <p className="text-xs text-red-500">{errors.SUBMODULE_TYPE}</p>
              )}
            </div> */}

            <ImageUploadField
              label="Sub Module Logo"
              value={previewImage}
              onChange={(preview, file) => {
                setPreviewImage(preview);
                handleChange("FILEDATA", file);
              }}
            />
          </div>

          {/* RIGHT */}
          <div className="space-y-4">
            <Input
              type="number"
              name="SUBMODULE_BASEPRICE"
              label="Base Price"
              icon={Hash}
              value={formData.SUBMODULE_BASEPRICE}
              onValueChange={handleChange}
            />

            <Input
              label="Pricing Type"
              name="SUBMODULE_BASEPRICETYPE"
              icon={Tag}
              value={formData.SUBMODULE_BASEPRICETYPE}
              onValueChange={handleChange}
              error={errors.SUBMODULE_BASEPRICETYPE}
            />

            <Input
              type="number"
              label="Per Seat Price"
              name="SUBMODULE_PERSEATPRICE"
              icon={Hash}
              value={formData.SUBMODULE_PERSEATPRICE}
              onValueChange={handleChange}
            />

            <Input
              type="number"
              name="SUBMODULE_USAGERATE"
              label="Usage Rate"
              icon={Percent}
              value={formData.SUBMODULE_USAGERATE}
              onValueChange={handleChange}
            />

            <Input
              type="number"
              label="Sequence No"
              name="SEQUENCE_NO"
              icon={Hash}
              value={formData.SEQUENCE_NO}
              onValueChange={handleChange}
            />
            <Input
              label="Trial Days"
              name="TRIAL_DAYS"
              icon={CalendarDays}
              type="number"
              value={formData.TRIAL_DAYS}
              onValueChange={handleChange}
            />
          </div>
        </div>

        {/* FOOTER */}
        <DialogFooter className="shrink-0 border-t border-slate-200 dark:border-slate-800 px-6 py-4 bg-slate-50 dark:bg-slate-900/50">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-teal-500 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Layers3 className="w-4 h-4" />
            )}

            {isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </ModalContent>
    </Dialog>
  );
}
